import LANG from '@ahoy/model/dist/utils/locale/lang';
import { ts } from '@ahoy/model/dist/utils/locale';
import {
  buildCustomerDocumentUploadLink,
  CUSTOMER_DOCUMENT_UPLOAD_SPA_PATH,
} from '@ahoy/model/dist/utils/customerDocumentUploadLink';

type Key = keyof typeof LANG;

// ---------------------------------------------------------------------------
// Customer-document-upload link substitution.
//
// The document-driven UW error messages in lang.ts each contain the inline
// phrase "click here to upload <noun>" — that span is where the deep link to
// the customer_document_upload micro-frontend should land.
//
// `substituteUploadLinks(message, errorCodes)` runs at composition chokepoints
// (formatWarningsAsBulletedMessage, getUWApprovalsErrorMessage, and the
// single-message static popups). It looks up each error code's required
// document type, finds the inline phrase(s) in the message, and either:
//   - single inline phrase     → wraps it in an <a> (link stays inline).
//   - multiple inline phrases  → replaces each with the multi-error helper
//     copy and appends a single combined link at the bottom of the message.
//
// applicationId access: callers register a provider via
// setUploadLinkApplicationIdProvider(). If no provider is registered, or the
// provider returns nothing, no link is built and the message is returned
// unchanged.
// ---------------------------------------------------------------------------

// Maps a UW error code to the customer_document_upload document type the link
// should target. Mirrors UW_ERROR_TO_DOC_CONFIG in common/context's
// UnderwritingApprovalsHandler.ts but kept inline here since the FE only needs
// it for link generation. Includes the agent variants (35A, 46A) so agent-side
// popups also get the link.
const UW_ERROR_DOCUMENT_TYPE: Record<string, string> = {
  UW_Error_35: 'Bill of Sale',
  UW_Error_35A: 'Bill of Sale',
  UW_Error_37: 'Bill of Sale',
  UW_Error_46: 'Bill of Sale',
  UW_Error_46A: 'Bill of Sale',
  UW_Error_51: 'Survey',
  UW_Error_52A: 'Bill of Sale',
  UW_Error_52B: 'Bill of Sale',
  UW_Error_56: 'Bill of Sale',
  // Client-side errorCodes used by the boat-value warning popup path (52A/52B's
  // pre-approval warnings). Same document type as UW_Error_52A/52B — mapped here so
  // substituteUploadLinks builds the BoS link when the popup renders.
  ValidOverrideValue: 'Bill of Sale',
  ValidMinOverrideValue: 'Bill of Sale',
};

// Matches the inline "click here to upload <noun>" phrase across all five
// document-driven UW error messages. Covers the noun-phrase variants:
//   35/46 (customer + agent): "these document/s"
//   37:                       "this document"
//   51:                       "the documents"
//   56:                       "these document"
const INLINE_UPLOAD_PHRASE_REGEX = /click here to upload (?:these document\/s|this document|the documents|these document)/gi;

const MULTI_ERROR_REPLACEMENT_PHRASE = 'please click the link below to upload documents';

let applicationIdProvider: () => string | undefined = () => undefined;

export function setUploadLinkApplicationIdProvider(fn: () => string | undefined) {
  applicationIdProvider = fn;
}

// Provider for document types that already have a Customer_Document record in
// Pending UW / Draft status against this application. Populated by
// insurance-service.ts off /insure/get-uw-approvals. When non-empty, a note is
// appended to the substituted message so the agent (or customer) knows the
// document is already in flight.
let pendingOrDraftDocumentTypesProvider: () => string[] = () => [];

export function setPendingOrDraftDocumentTypesProvider(fn: () => string[]) {
  pendingOrDraftDocumentTypesProvider = fn;
}

// Provider for whether the current application is a renewal. The pending-docs
// note is only useful in the renewal flow (where docs may have been carried
// forward / re-supplied against the prior policy); on new business / MTA it
// would just be confusing, so we gate the note on this.
let isRenewalProvider: () => boolean = () => false;

export function setIsRenewalProvider(fn: () => boolean) {
  isRenewalProvider = fn;
}

// Joins doc types into a human-readable list with Oxford-free "and" tail.
//   ['Survey']                              -> 'Survey'
//   ['Survey', 'Bill of Sale']              -> 'Survey and Bill of Sale'
//   ['Survey', 'Bill of Sale', 'Trailer']   -> 'Survey, Bill of Sale and Trailer'
const formatDocTypeList = (items: string[]): string => {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

const resolveCustomerDocumentUploadOrigin = (): string => {
  if (typeof window === 'undefined') return '';
  // Localhost dev: lighthouse runs on :8000 but the customer_document_upload
  // SPA dev server is on :8004 (mirrors the same swap captain's resolveBaseUrl
  // does on the backend side). In prod the two ship from the same origin
  // under different paths, so window.location.origin is correct.
  const { hostname, origin } = window.location;
  if (/^(localhost|127\.0\.0\.1)$/.test(hostname)) return 'http://localhost:8004';
  return origin;
};

const buildLinkUrl = (documentTypes: string[]): string | null => {
  const applicationId = applicationIdProvider();
  if (!applicationId || documentTypes.length === 0) return null;
  const baseUrl = resolveCustomerDocumentUploadOrigin() + CUSTOMER_DOCUMENT_UPLOAD_SPA_PATH;
  const linkResult = buildCustomerDocumentUploadLink(baseUrl, { applicationId, documentType: documentTypes });
  return linkResult.ok ? linkResult.link : null;
};

// Substitutes the inline upload phrase(s) in a composed message with a real
// link. Behavior depends on how many phrases the message contains:
//   - 0 occurrences  → return as-is
//   - 1 occurrence   → wrap the phrase in an anchor (link stays inline)
//   - 2+ occurrences → replace each with the multi-error helper copy and
//                      append one combined-doc-types link at the bottom
//
// Unquoted href/target attributes are intentional — non-eligible-popup runs a
// `"` → `&quot;` pass before unsafeHTML which would otherwise break the URL.
// URLSearchParams encoding ensures the URL has no whitespace or `>` that
// would prematurely terminate an unquoted attribute value.
export const substituteUploadLinks = (message: string, errorCodes: string[]): string => {
  if (!message) return message;

  const docTypes = Array.from(new Set(
    errorCodes.map((code) => UW_ERROR_DOCUMENT_TYPE[code]).filter(Boolean),
  ));
  if (docTypes.length === 0) return message;

  const linkUrl = buildLinkUrl(docTypes);
  const phraseMatches = message.match(INLINE_UPLOAD_PHRASE_REGEX) || [];

  let result = message;
  if (linkUrl) {
    if (phraseMatches.length === 1) {
      result = message.replace(
        INLINE_UPLOAD_PHRASE_REGEX,
        (match) => `<a href=${linkUrl} target=_blank>${match}</a>`,
      );
    } else if (phraseMatches.length > 1) {
      const stripped = message.replace(INLINE_UPLOAD_PHRASE_REGEX, MULTI_ERROR_REPLACEMENT_PHRASE);
      result = `${stripped} <br><br><a href=${linkUrl} target=_blank>Click here to upload your documents</a>`;
    } else {
      // Defensive: errorCodes claim a link is needed but the message has no
      // inline phrase. Append at the bottom so the link is still reachable.
      result = `${message} <br><br><a href=${linkUrl} target=_blank>Click here to upload your documents</a>`;
    }
  }

  // Renewal-only: surface in-progress uploads so the reader doesn't
  // double-upload (and agents know the customer side is already taken care of).
  // On new business / MTA this note would be confusing, so skip it.
  if (isRenewalProvider()) {
    const pendingDocs = pendingOrDraftDocumentTypesProvider() || [];
    if (pendingDocs.length > 0) {
      const verb = pendingDocs.length > 1 ? 'they are' : 'it is';
      result = `${result} <br><br>Please note that the customer has already provided ${formatDocTypeList(pendingDocs)} and ${verb} now pending review`;
    }
  }

  return result;
};

export function t(v: Key | string, ...rest: any[]) {
  // this function looks for the agent text key in the LANG object and returns its value if exists
  const checkAgentText = (v: string) => {
    const userType = (window as any)['userType'];
    if (userType == 'agent' || userType == 'externalAgent') {
      const agentTextKey = v + '_AGENT';
      const isAgentTextKeyFound = String(LANG[agentTextKey as Key] || '');
      if (isAgentTextKeyFound) return ts(agentTextKey, ...rest);
    }
    return '';
  };
  // this function checks if the user is an agent and if so, it returns the agent text if it exists
  let agentText: string = checkAgentText(v);
  const userText = ts(v, ...rest);
  // No link substitution here — callers run substituteUploadLinks() at their
  // composition chokepoint so multi-error popups get a single deduplicated link.
  return agentText || userText;
};
